import SelectFilter from "@/components/customer-page-components/filters/SelectFilter";
import { Colors } from "@/enums/Colors";
import { useProductServiceMap } from "@/hooks/queries/lazo-product/useLazoProductQueries";
import { ProductModule } from "@/types/ProductModule";
import { FilterCategoryType } from "@/utils/FiltersUtils";
import { Add } from "@mui/icons-material";
import { Button, menuItemClasses, Popover, PopoverProps, styled, svgIconClasses, Typography, typographyClasses } from "@mui/material";
import MenuItem from "@mui/material/MenuItem";
import { Dispatch, SetStateAction, useEffect, useState } from "react";

const StyledButton = styled(Button)(() => ({
  minHeight: "2.5rem",
  minWidth: "2.5rem",
  maxHeight: "2.5rem",
  maxWidth: "2.5rem",
  borderRadius: "0.500rem",
  border: `1px solid ${Colors.STROKE_DARK_GRAY}`,
  ":hover": {
    backgroundColor: `${Colors.ALMOST_WHITE}`,
  },
}));

const StyledPopover = styled(({ children, ...props }: PopoverProps) => (
  <Popover
    slotProps={{
      paper: {
        sx: {
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
          justifyContent: "center",
          backgroundColor: "#fff",
          borderRadius: "0.25rem",
          padding: "0",
          boxShadow: "0 0 0.975rem 0 rgba(0, 0, 0, 0.2)",
          width: "13.75rem",
        },
      },
    }}
    {...props}
  >
    {children}
  </Popover>
))(() => ({
  top: 4,
  [`& .${typographyClasses.root}`]: {
    fontSize: "0.75rem",
    fontWeight: "400",
  },
  [`& .${menuItemClasses.root}`]: {
    width: "100%",
    justifyContent: "space-between",
    [`& .${svgIconClasses.root}`]: {
      color: Colors.LIGHT_GRAY_8A8E93,
      fontSize: "0.875rem",
    },
  },
}));

interface AddFilterButtonProps {
  addedFilterCategories: FilterCategoryType<unknown>[];
  setAddedFilterCategories: Dispatch<SetStateAction<FilterCategoryType<unknown>[]>>;
  selectedFilters: FilterCategoryType<unknown>[];
  setSelectedFilters: Dispatch<SetStateAction<FilterCategoryType<unknown>[]>>;
  filterCategories: FilterCategoryType<unknown>[];
}

export default function AddFilterButton(props: AddFilterButtonProps) {
  const [anchor, setAnchor] = useState<HTMLButtonElement | null>(null);
  const { data: productServiceMap, isLoading, isError } = useProductServiceMap();

  useEffect(() => {
    if (props.filterCategories.length == props.addedFilterCategories.length) {
      setAnchor(null);
    }
  }, [props.addedFilterCategories]);

  const open = Boolean(anchor);
  const id = open ? "filters-popover" : undefined;
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchor(event.currentTarget);
  };
  const handleClose = () => setAnchor(null);

  const handleFilterClick = (ev: React.MouseEvent<HTMLLIElement>) => {
    const filterCategory = props.filterCategories[ev.currentTarget.value];
    if (!props.addedFilterCategories.find((v) => v.id === filterCategory.id)) {
      props.setSelectedFilters([...props.selectedFilters, { ...filterCategory, filters: [] }]);
      props.setAddedFilterCategories([...props.addedFilterCategories, filterCategory]);
    }
  };

  const getServicesByModuleSelected = () => {
    if (!productServiceMap) return [];

    const modulesSelected = props.selectedFilters.find((m) => m.id === "modules")?.filters;
    return modulesSelected?.length
      ? modulesSelected
          .map((m) => productServiceMap[m as ProductModule] || [])
          .flat()
          .map((s) => s.value)
      : Object.values(productServiceMap)
          .flat()
          .map((s) => s.value);
  };

  const getFilterCategory = (category: FilterCategoryType<unknown>) => {
    switch (category.id) {
      case "services":
        return { ...category, filters: getServicesByModuleSelected() };
      default:
        return category;
    }
  };

  if (isLoading) {
    return null; // Or a loading spinner if desired
  }

  if (isError || !productServiceMap) {
    return null; // Silently fail - filters won't be available
  }

  return (
    <>
      {props.addedFilterCategories.map((category) => (
        <SelectFilter
          key={category.id}
          filterCategory={getFilterCategory(category)}
          selectedCategoryFilters={props.selectedFilters.find((fc) => fc.id === category.id).filters}
          setAddedFilterCategories={props.setAddedFilterCategories}
          setSelectedFilters={props.setSelectedFilters}
        />
      ))}
      {props.filterCategories.length !== props.addedFilterCategories.length && (
        <StyledButton aria-describedby={id} onClick={handleClick}>
          <Add />
        </StyledButton>
      )}
      <StyledPopover
        id={id}
        open={props.filterCategories.length !== props.addedFilterCategories.length && open}
        anchorEl={anchor}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
      >
        {props.filterCategories.map((filter, idx) => (
          <MenuItem key={filter.id} value={idx} onClick={handleFilterClick} sx={{ padding: "0.5rem 1rem" }}>
            <Typography>{filter.render}</Typography>
            <Add />
          </MenuItem>
        ))}
      </StyledPopover>
    </>
  );
}
